import clsx from 'clsx';
import { type ComponentPropsWithoutRef, type ForwardedRef } from 'react';
import { HeadingContext } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';
import { DescriptionContext } from '../Description';

export interface SidebarAlertProps extends ComponentPropsWithoutRef<'div'> {
    /** Shows a minimize button that calls this handler. The consumer owns the collapsed state. */
    onCollapse?: () => void;
}

/**
 * Compact, composition-based alert surface for sidebar content. Unlike {@link Alert}, it inherits
 * heading and description styles from context and lets consumers compose their own actions.
 */
export function SidebarAlert({
    ref,
    ...props
}: SidebarAlertProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    const { children, className, onCollapse, ...rest } = props;
    return (
        <div
            ref={ref}
            {...rest}
            className={clsx(
                'flex flex-col gap-3 rounded-md bg-base p-3 shadow-sm',
                onCollapse && 'relative',
                className
            )}
        >
            {onCollapse ? (
                <Button
                    kind="blank"
                    size="xsmall"
                    iconOnly
                    label="Minimize alert"
                    leadingIcon={Icon.Minus}
                    className="absolute top-1.5 right-1.5"
                    onPress={onCollapse}
                />
            ) : null}
            <HeadingContext.Provider
                value={{
                    level: 3,
                    className: clsx('text-ui-small', onCollapse && 'pr-5'),
                }}
            >
                <DescriptionContext.Provider value={{ className: '-mt-2 text-ui-small' }}>
                    {children}
                </DescriptionContext.Provider>
            </HeadingContext.Provider>
        </div>
    );
}

/**
 * Layout container for actions rendered at the bottom of a {@link SidebarAlert}.
 */
export function SidebarAlertActions(props: ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            {...props}
            className={clsx(
                'flex w-full flex-wrap items-center justify-end gap-1',
                props.className
            )}
        />
    );
}
